import type React from "react"

import type { AnimationPlayer } from "@/types"

export interface PlayerLookupEntry {
  name: string
  playerId: string
}

/**
 * Builds a lookup array from the AnimationData players map,
 * sorted longest-name-first so regex alternation matches greedily.
 */
export function buildPlayerLookup(
  players: Record<string, AnimationPlayer>,
): PlayerLookupEntry[] {
  return Object.entries(players)
    .map(([id, p]) => ({ name: p.name, playerId: id }))
    .sort((a, b) => b.name.length - a.name.length)
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/**
 * Renders a text string with player names wrapped in hoverable spans
 * that trigger the gold pitch highlight on mouseenter/mouseleave.
 *
 * Uses regex splitting with a capture group so matched segments interleave
 * with plain-text segments. Matched names get the same golden dashed
 * underline as the header player name.
 */
export function renderTextWithPlayerLinks(
  text: string,
  lookup: PlayerLookupEntry[] | undefined,
  onPlayerHover: ((playerId: string) => void) | undefined,
  onPlayerHoverEnd: (() => void) | undefined,
): React.ReactNode {
  if (!text || !lookup || lookup.length === 0) return text

  // Build a single regex with all names as alternations (longest first)
  const pattern = lookup.map((e) => escapeRegex(e.name)).join("|")
  const regex = new RegExp(`(${pattern})`, "g")

  const segments = text.split(regex)
  if (segments.length === 1) return text

  // Build a name→playerId map for fast lookup of matched segments
  const nameToId = new Map(lookup.map((e) => [e.name, e.playerId]))

  return segments.map((segment, i) => {
    const playerId = nameToId.get(segment)
    if (playerId) {
      return (
        <span
          key={i}
          onMouseEnter={
            onPlayerHover ? () => onPlayerHover(playerId) : undefined
          }
          onMouseLeave={onPlayerHoverEnd}
          style={{
            borderBottom: "1px dashed oklch(0.60 0.12 85)",
            cursor: onPlayerHover ? "pointer" : undefined,
          }}
        >
          {segment}
        </span>
      )
    }
    return segment
  })
}
